/**
 * roxin-motion.js
 * ---------------------------------------------------------------------------
 * Arquivo ÚNICO de movimento para as seções customizadas da Roxin.
 *
 * Por que um arquivo só: cada seção poderia trazer o seu próprio script, mas
 * isso multiplica requisições e repete o mesmo IntersectionObserver várias
 * vezes na página. Aqui tudo compartilha os mesmos observers.
 *
 * Responsabilidades:
 *   1. Revelar elementos [data-rx-reveal] quando entram na tela (com stagger via
 *      a custom property --rx-delay definida no HTML).
 *   2. Controlar vídeos de fundo do hero: preload none, só toca quando
 *      visível, e por padrão NÃO toca no mobile.
 *   3. Controlar os vídeos verticais da seção de cards de vídeo.
 *   4. Controlar o vídeo da vitrine do produto (garrafa 360).
 *
 * Tudo respeita `prefers-reduced-motion`. É idempotente: pode rodar de novo
 * depois de o editor de temas recarregar uma seção, sem duplicar efeito.
 */
(() => {
  /*
    Cada seção que usa movimento inclui a sua própria tag <script>. Se várias
    estiverem na mesma página, o navegador baixa o arquivo uma vez (mesma URL,
    vem do cache) mas EXECUTA o script uma vez por tag. Sem esta guarda,
    criaríamos um IntersectionObserver por execução.

    A primeira execução já registra o listener de `shopify:section:load`, então
    seções carregadas depois pelo editor continuam sendo inicializadas.
  */
  if (window.__roxinMotionReady) return;
  window.__roxinMotionReady = true;

  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const MOBILE_QUERY = '(max-width: 749px)';

  /* ---------------------------------------------------------------------
     1. Reveal on scroll

     O gancho é o ATRIBUTO [data-rx-reveal], não uma classe. Motivo: cada
     seção do tema carrega o seu próprio CSS, então uma classe global
     compartilhada entre seções não é garantida — cada seção define as
     regras de animação no seu próprio bloco de estilos, escopadas.
     --------------------------------------------------------------------- */
  const revealObserver = REDUCED
    ? null
    : new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-visible');
            revealObserver.unobserve(entry.target);
          });
        },
        { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
      );

  const initReveals = (root = document) => {
    const items = root.querySelectorAll('[data-rx-reveal]:not([data-rx-reveal-ready])');
    items.forEach((el) => {
      el.setAttribute('data-rx-reveal-ready', 'true');
      if (REDUCED || !revealObserver) {
        el.classList.add('is-visible');
        return;
      }
      revealObserver.observe(el);
    });
  };

  /* ---------------------------------------------------------------------
     2. Vídeo de fundo do hero
     --------------------------------------------------------------------- */
  const safePlay = (video) => {
    const attempt = video.play();
    if (attempt && typeof attempt.catch === 'function') {
      // Autoplay pode ser bloqueado pelo navegador. Não é erro: o poster
      // continua visível e a página segue funcionando.
      attempt.catch(() => {});
    }
  };

  const initHeroVideos = (root = document) => {
    const heroes = root.querySelectorAll('[data-rx-hero]:not([data-rx-hero-ready])');

    heroes.forEach((hero) => {
      hero.setAttribute('data-rx-hero-ready', 'true');

      const holder = hero.querySelector('.rx-hero__video-holder');
      const video = hero.querySelector('.rx-hero__video');
      if (!holder || !video) return;

      const playOnMobile = hero.dataset.playMobile === 'true';

      const allowed = () => {
        if (REDUCED) return false;
        const isMobile = window.matchMedia(MOBILE_QUERY).matches;
        if (isMobile && !playOnMobile) return false;
        return true;
      };

      let inView = false;

      const sync = () => {
        if (inView && allowed()) {
          safePlay(video);
          holder.classList.add('is-playing');
        } else {
          video.pause();
          holder.classList.remove('is-playing');
        }
      };

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            inView = entry.isIntersecting;
            sync();
          });
        },
        { threshold: 0.2 }
      );

      observer.observe(hero);

      let resizeTimer;
      window.addEventListener(
        'resize',
        () => {
          clearTimeout(resizeTimer);
          resizeTimer = setTimeout(sync, 200);
        },
        { passive: true }
      );
    });
  };

  /* ---------------------------------------------------------------------
     3. Cards de vídeo vertical
     --------------------------------------------------------------------- */
  const initVideoCards = (root = document) => {
    const groups = root.querySelectorAll('[data-rx-video-cards]:not([data-rx-video-ready])');

    groups.forEach((group) => {
      group.setAttribute('data-rx-video-ready', 'true');

      const cards = Array.from(group.querySelectorAll('[data-rx-video-card]'));
      if (!cards.length) return;

      if (REDUCED) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const video = entry.target.querySelector('video');
            if (!video) return;
            if (entry.isIntersecting) {
              safePlay(video);
            } else {
              video.pause();
            }
          });
        },
        // 55% garante que só o card realmente em foco toca — evita 4 vídeos
        // rodando ao mesmo tempo num carrossel.
        { threshold: 0.55 }
      );

      cards.forEach((card) => observer.observe(card));
    });
  };

  /* ---------------------------------------------------------------------
     4. Vitrine de vídeo do produto (garrafa 360)

     Vivia em assets/product-video-showcase.js, que fazia exatamente o mesmo
     que as funções acima: IntersectionObserver + play/pause + respeito ao
     prefers-reduced-motion. Dois arquivos separados significavam duas
     requisições e dois conjuntos de observers numa página que carrega os
     dois. Consolidado aqui.

     Quando há versão de desktop e de mobile, só a que estiver VISÍVEL toca.
     Como ambas usam preload="none", a escondida nunca chega a ser baixada.
     --------------------------------------------------------------------- */
  const initShowcaseVideos = (root = document) => {
    const wrappers = root.querySelectorAll(
      '[data-roxin-video-trigger]:not([data-rx-showcase-ready])'
    );

    wrappers.forEach((wrapper) => {
      wrapper.setAttribute('data-rx-showcase-ready', 'true');

      const videos = Array.from(wrapper.querySelectorAll('video'));
      if (!videos.length) return;

      if (REDUCED) {
        videos.forEach((v) => {
          v.pause();
          v.removeAttribute('autoplay');
        });
        return;
      }

      /* Só conta como visível o vídeo que ocupa espaço no layout. */
      const visivel = (el) => !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);

      let inView = false;

      const sync = () => {
        videos.forEach((v) => {
          if (inView && visivel(v)) safePlay(v);
          else v.pause();
        });
      };

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            inView = entry.isIntersecting;
            sync();
          });
        },
        { threshold: 0.35 }
      );

      observer.observe(wrapper);

      let resizeTimer;
      window.addEventListener(
        'resize',
        () => {
          clearTimeout(resizeTimer);
          resizeTimer = setTimeout(sync, 200);
        },
        { passive: true }
      );
    });
  };

  /* ---------------------------------------------------------------------
     Bootstrap
     --------------------------------------------------------------------- */
  const initAll = (root = document) => {
    initReveals(root);
    initHeroVideos(root);
    initVideoCards(root);
    initShowcaseVideos(root);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  // O editor de temas recarrega seções individualmente via este evento.
  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });
})();
